import * as React from 'react'
import { FC, useEffect, useState, useCallback } from 'react'

import { InfoTooltip, SettingsPage } from '@sales-center/components'

import { updateLoadingState } from '@sales-center/common'
import { useGetBranchListOnMount } from '@user/store/branches/hooks/useGetBranchListOnMount'
import { useBranchSearch } from '@user/store/branches/hooks/useBranchSearch'

import { BranchDetail } from './BranchDetail'
import { getStyles } from './hooks/useStyles'
import { useGetCompanySetupOnMount } from './hooks/useGetCompanySetupOnMount'
import { useSelector } from 'react-redux'
import { selectCompanyInfo } from '@user/store/companySetup/slice'
import { CompanyInfo } from './CompanyInfo'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'

import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'

export const CompanySetup: FC = () => {
  const dispatch = useAppDispatch()

  const [search, setSearch] = useState<string>()

  const companyStatus = useGetCompanySetupOnMount()
  const company = useSelector(selectCompanyInfo)

  const branchStatus = useGetBranchListOnMount()
  const branches = useBranchSearch(search)

  //#region search event handlers
  const onChangeSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setSearch(event.target.value),
    [],
  )

  const onCancelSearch = useCallback(() => setSearch(''), [])
  //#endregion search

  useEffect(() => {
    const loading = companyStatus == 'pending' || branchStatus == 'pending'
    dispatch(updateLoadingState(loading ? 'visible' : 'hidden'))
  }, [dispatch, branchStatus, companyStatus])
  const styles = getStyles(useTheme())
  return (
    <SettingsPage
      title={
        <span style={styles.title}>
          Company Setup
          <InfoTooltip
            sx={styles.info}
            infoText="Updated company / branch information is populated from PestPac."
            tooltipPlacement="right"
          />
        </span>
      }
      headContent={
        <TextField
          fullWidth
          value={search}
          onChange={onChangeSearch}
          sx={styles.search}
          placeholder="Search for Branch"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={onCancelSearch}
                  data-test-id="companySetupSearchClearBtn"
                >
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      }
      padding="0"
    >
      <Box sx={styles.mainBranchSection}>
        <Typography sx={styles.mainBranchTitle}>Main Branch</Typography>
        <CompanyInfo companySetup={company} />
      </Box>
      {branches.map((b) => (
        <BranchDetail key={b.id} id={b.id} name={b.name} />
      ))}
    </SettingsPage>
  )
}
CompanySetup.displayName = 'CompanySetup'
